import { useCallback, useEffect, useState } from "react";
import { fetchPortfolioHistory } from "../services/api.js";

/**
 * Portfolio-value-over-time series for the Dashboard's Total Returns
 * chart. Backend (transaction.controller.js → portfolioHistory) replays
 * the wallet's deposit lifecycle and returns the samples — frontend
 * just renders them, so multiple dashboard surfaces converge on the
 * same numbers.
 */
export function usePortfolioHistory(owner, { windowId = "7d", samples = 61, pollMs = 30_000 } = {}) {
    const [data, setData] = useState(null);
    const [isLoading, setLoading] = useState(Boolean(owner));
    const [error, setError] = useState(null);

    // `opts.background === true` = silent poll refresh: keep the current
    // chart on screen while re-fetching so it never flips back to the
    // loading state on the 30s poll. Only foreground calls toggle loading
    // / null the data on error.
    const refresh = useCallback(async (opts = {}) => {
        const background = opts.background === true;
        if (!owner) {
            setData(null);
            setLoading(false);
            return;
        }
        if (!background) {
            setLoading(true);
            setError(null);
        }
        try {
            const resp = await fetchPortfolioHistory(owner, { windowId, samples });
            setData({
                samples: (resp.samples || []).map((s) => ({
                    timestamp: Number(s.timestamp),
                    value: BigInt(s.value || "0")
                })),
                peak: resp.peak
                    ? { timestamp: Number(resp.peak.timestamp), value: BigInt(resp.peak.value || "0") }
                    : null,
                windowId: resp.windowId,
                fromTime: resp.fromTime,
                toTime: resp.toTime
            });
            setError(null);
        } catch (err) {
            setError(err?.message || "Failed to fetch portfolio history");
            if (!background) setData(null);
        } finally {
            if (!background) setLoading(false);
        }
    }, [owner, windowId, samples]);

    useEffect(() => {
        refresh();
        if (!pollMs) return undefined;
        const handle = setInterval(() => refresh({ background: true }), pollMs);
        return () => clearInterval(handle);
    }, [refresh, pollMs]);

    return { data, isLoading, error, refresh };
}
